import { useState, useEffect } from 'react';
import { format } from 'date-fns';

interface SponsorshipConfigProps {
  onConfigChange: (config: {
    sponsored: boolean;
    sponsorshipStartDate: string;
    sponsorshipEndDate: string;
    pricingType: 'click' | 'sale';
    pricingValue: number;
  } | null) => void;
}

export default function SponsorshipConfig({ onConfigChange }: SponsorshipConfigProps) {
  const [pricingType, setPricingType] = useState<'click' | 'sale'>('sale');
  const [pricingValue, setPricingValue] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [error, setError] = useState('');

  // Validar e atualizar configuração sempre que houver mudanças
  useEffect(() => {
    validateAndUpdate();
  }, [pricingType, pricingValue, startDate, endDate]);

  const validateAndUpdate = () => {
    // Resetar erro
    setError('');

    // Validar valor mínimo
    const numericValue = Number(pricingValue);
    if (pricingType === 'click' && numericValue < 0.17) {
      setError('O valor mínimo por clique é €0.17');
      onConfigChange(null);
      return;
    }
    if (pricingType === 'sale' && numericValue < 5) {
      setError('A porcentagem mínima por venda é 5%');
      onConfigChange(null);
      return;
    }

    // Validar datas
    if (!startDate || !endDate) {
      onConfigChange(null);
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    if (end <= start) {
      setError('A data de término deve ser posterior à data de início');
      onConfigChange(null);
      return;
    }

    // Se passou por todas as validações, atualizar configuração
    onConfigChange({
      sponsored: true,
      sponsorshipStartDate: startDate,
      sponsorshipEndDate: endDate,
      pricingType,
      pricingValue: numericValue
    });
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'start' | 'end') => {
    e.preventDefault();
    e.stopPropagation();
    const value = e.target.value;
    
    if (type === 'start') {
      setStartDate(value);
    } else {
      setEndDate(value);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        {/* Tipo de Precificação */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tipo de Precificação
          </label>
          <div className="flex gap-4">
            <label className="flex items-center opacity-50 cursor-not-allowed">
              <input
                type="radio"
                name="pricingType"
                value="click"
                disabled
                className="mr-2"
              />
              Por Clique <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">Em breve</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="pricingType"
                value="sale"
                checked={true}
                onChange={(e) => setPricingType('sale')}
                className="mr-2"
              />
              Por Venda
            </label>
          </div>
        </div>

        {/* Valor */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {pricingType === 'sale' ? 'Porcentagem por Venda (%)' : 'Valor por Clique (€)'}
          </label>
          <div className="relative">
            <input
              type="number"
              value={pricingValue}
              onChange={(e) => setPricingValue(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder={pricingType === 'sale' ? 'Ex: 10' : 'Ex: 0.20'}
              min={pricingType === 'sale' ? 5 : 0.17}
              step={pricingType === 'sale' ? 1 : 0.01}
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <span className="text-gray-500">{pricingType === 'sale' ? '%' : '€'}</span>
            </div>
          </div>
          {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
        </div>

        {/* Período */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Período do Patrocínio
          </label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Data Inicial</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => handleDateChange(e, 'start')}
                min={format(new Date(), 'yyyy-MM-dd')}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Data Final</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => handleDateChange(e, 'end')}
                min={startDate || format(new Date(), 'yyyy-MM-dd')}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
